import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Compass, Thermometer, X, Loader2, Target,
  ArrowUp, ArrowDown, Minus, HelpCircle, Radio,
} from "lucide-react";
import { playSignalBurst } from "@/lib/signal";

/**
 * Warmer/Colder GPS hint button.
 *
 * Calls /api/drops/:id/hint and renders:
 *   • Big band label + color
 *   • Trend chip (↑ heating up / ↓ cooling down / = same zone)
 *   • Range text (e.g. "Under ~655 ft") so hunters know what the band means
 *   • Expandable legend with the full warmer/colder scale
 *
 * The exact distance is still never revealed — only the band's upper bound.
 */
export default function GpsHintButton({ dropId, endpoint, testid = "hint-button", noun = "drop", preview = false, previewToken = "" }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [showLegend, setShowLegend] = useState(false);

  const url = endpoint || `/drops/${dropId}/hint`;

  const requestHint = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device.");
      return;
    }
    setLoading(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const params = preview
            ? { preview: 1 }
            : (previewToken ? { preview_token: previewToken } : null);
          const { data } = await api.post(
            url,
            { lat: pos.coords.latitude, lng: pos.coords.longitude },
            params ? { params } : undefined,
          );
          setResult(data);
          if (data && data.signal_active) {
            try { playSignalBurst(data.signal_level || 1); } catch { /* noop */ }
          }
        } catch (e) {
          setError(formatApiError(e.response?.data?.detail) || "Hint failed");
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setLoading(false);
        setError(err.message || "Location permission denied");
        toast.error("Enable location to use hints.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const bandCopy = (b) => ({
    hot: "You're right on top of it — look carefully.",
    warmer: "Very close. Sweep the area.",
    warm: "Short walk away.",
    colder: "Keep moving — you're inside the drop zone.",
    cold: "Wrong area. Re-read the clues.",
  })[b] || "";

  const trendMeta = (t) => {
    if (t === "up")   return { Icon: ArrowUp,   text: "Hotter than last check", color: "#E53935" };
    if (t === "down") return { Icon: ArrowDown, text: "Cooler than last check", color: "#1e88e5" };
    if (t === "same") return { Icon: Minus,     text: "Same zone — keep moving", color: "#F5F2E8" };
    return null;
  };

  return (
    <div className="mt-6">
      <div className="loot-card-flat p-5" style={{ borderColor: "#D7A62A" }}>
        <div className="flex items-center gap-2 mb-2">
          <Compass size={16} className="text-[#D7A62A]" />
          <span className="overline">Warmer / colder</span>
          <button
            type="button"
            onClick={() => setShowLegend((v) => !v)}
            className="ml-auto text-[#F5F2E8]/50 hover:text-[#F5F2E8]"
            aria-label="Show scale"
            data-testid="hint-legend-toggle"
          >
            <HelpCircle size={14} />
          </button>
        </div>
        <p className="text-sm text-[#F5F2E8]/70">
          Tap to check your distance to the hiding spot. We'll never tell you
          the exact spot — only whether you're getting closer.
        </p>

        {/* ---------- Bounty portal result ---------- */}
        {result && result.band === "bounty_portal" && (
          <div
            className="mt-4 border p-4"
            style={{ borderColor: result.color, background: `${result.color}15` }}
            data-testid="hint-result-bounty"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <Target size={26} style={{ color: result.color }} className="shrink-0 mt-1" />
                <div>
                  <div className="font-display uppercase text-2xl leading-tight" style={{ color: result.color }}>
                    {result.label}
                  </div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest mt-1">
                    Bounty drop · sponsor reveal
                  </div>
                  {result.sponsor?.business_name && (
                    <div className="mt-2 text-sm text-[#F5F2E8]">
                      Head into{" "}
                      <span className="font-display uppercase" style={{ color: result.color }}>
                        {result.sponsor.business_name}
                      </span>
                      {result.sponsor.city ? ` · ${result.sponsor.city}` : ""}
                    </div>
                  )}
                  {result.bounty_instructions && (
                    <p className="mt-2 text-sm text-[#F5F2E8]/80 italic">
                      "{result.bounty_instructions}"
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setResult(null)}
                className="text-[#F5F2E8]/50 shrink-0"
                aria-label="Dismiss hint"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ---------- Warmer/colder result ---------- */}
        {result && result.band !== "bounty_portal" && (
          <div
            className="mt-4 border p-4"
            style={{ borderColor: result.color, background: `${result.color}15` }}
            data-testid="hint-result"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <Thermometer size={26} style={{ color: result.color }} className="shrink-0 mt-1" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div
                      className="font-display uppercase text-2xl leading-tight"
                      style={{ color: result.color }}
                    >
                      {result.label}
                    </div>
                    {(() => {
                      const t = trendMeta(result.trend);
                      if (!t) return null;
                      const { Icon, text, color } = t;
                      return (
                        <span
                          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border"
                          style={{ color, borderColor: color }}
                          data-testid="hint-trend"
                          title={text}
                        >
                          <Icon size={11} /> {result.trend === "up" ? "Hotter" : result.trend === "down" ? "Colder" : "Same"}
                        </span>
                      );
                    })()}
                  </div>

                  {result.range_max_ft != null && (
                    <div
                      className="font-mono text-[11px] text-[#F5F2E8]/85 mt-1"
                      data-testid="hint-range"
                    >
                      {result.band === "colder"
                        ? `Somewhere within ~${result.range_max_ft.toLocaleString()} ft of the spot`
                        : `Under ~${result.range_max_ft.toLocaleString()} ft from the spot`}
                    </div>
                  )}

                  <div className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest mt-2">
                    {bandCopy(result.band)}
                  </div>
                  {result.signal_active && (
                    <div
                      className="mt-3 inline-flex items-center gap-2 px-2 py-1 border"
                      style={{ borderColor: "#E53935", background: "#E5393525" }}
                      data-testid="signal-active"
                      title="The Signal is locked on — listen and feel the pulse"
                    >
                      <Radio size={12} className="signal-pulse" style={{ color: "#E53935" }} />
                      <span className="font-display uppercase text-[11px] tracking-widest" style={{ color: "#E53935" }}>
                        Signal locked · L{result.signal_level || 1}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setResult(null)}
                className="text-[#F5F2E8]/50 shrink-0"
                aria-label="Dismiss hint"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ---------- Scale / Legend ---------- */}
        {showLegend && (
          <div
            className="mt-4 border border-[#F5F2E8]/15 p-3"
            data-testid="hint-legend"
          >
            <div className="overline mb-2">What the colors mean</div>
            <div className="space-y-1.5">
              {(result?.all_bands || DEFAULT_BANDS).map((b) => (
                <div key={b.key} className="flex items-center gap-3 text-xs">
                  <span
                    className="font-display uppercase px-2 py-0.5 w-[130px] text-center"
                    style={{ background: b.color, color: "#0F0F0F" }}
                  >
                    {b.label}
                  </span>
                  <span className="text-[#F5F2E8]/75 font-mono">
                    {b.range_text}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-2 font-mono text-[10px] text-[#F5F2E8]/50 uppercase tracking-widest">
              Ranges are approximate. Never reveals the exact spot.
            </p>
          </div>
        )}

        {error && <div className="mt-3 font-mono text-xs text-[#E53935]">{error}</div>}

        <button
          onClick={requestHint}
          disabled={loading}
          className="btn-gold mt-4 inline-flex items-center gap-2"
          data-testid={testid}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Compass size={14} />}
          {loading ? "Getting hint…" : result ? "Check again" : "Get hint"}
        </button>
      </div>
    </div>
  );
}

// Fallback shown in the legend before the user has taken any reading.
const DEFAULT_BANDS = [
  { key: "hot", label: "ON FIRE", color: "#E53935", range_text: "Under ~165 ft" },
  { key: "warmer", label: "GETTING WARMER", color: "#D7A62A", range_text: "Under ~655 ft" },
  { key: "warm", label: "WARM", color: "#D7A62A", range_text: "Under ~1,640 ft (~⅓ mi)" },
  { key: "colder", label: "COLDER", color: "#76B82A", range_text: "Inside the drop zone" },
  { key: "cold", label: "WAY OFF", color: "#1e88e5", range_text: "Outside the zone" },
];
